import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { useAuth } from '@/lib/auth'
import { useI18n } from '@/lib/i18n'
import { useTRPC } from '@/lib/trpc'
import { useQuery } from '@tanstack/react-query'
import { Gift, MessageSquareText, RefreshCw, ReceiptText } from 'lucide-react'

function formatCost(microdollars: number) {
  return `$${(microdollars / 1_000_000).toFixed(4)}`
}

export function ReviewUsageCard() {
  const { user } = useAuth()
  const { t } = useI18n()
  const trpc = useTRPC()
  const { data, isLoading } = useQuery({
    ...trpc.reviewRouter.getUsageSummary.queryOptions(),
    enabled: !!user,
  })

  const weekly = data?.weekly
  const sessions = data?.sessions ?? []
  const progress = weekly && weekly.limit > 0
    ? Math.min(100, Math.round((weekly.used / weekly.limit) * 100))
    : 0

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquareText className="size-5" />
          {t('auth.dashboard.reviewUsageTitle')}
        </CardTitle>
        <CardDescription>
          {t('auth.dashboard.reviewUsageDescription')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {isLoading || !weekly ? (
          <div className="flex items-center justify-center py-10">
            <RefreshCw className="size-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div
              className="space-y-2 rounded-lg border bg-muted/20 p-4"
              data-testid="review-weekly-free-usage"
            >
              <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <span className="flex items-center gap-2 font-medium">
                  <Gift className="size-4 text-emerald-500" />
                  {t('auth.dashboard.reviewUsageWeeklyFree')}
                </span>
                <span className="font-semibold tabular-nums">
                  {t('auth.dashboard.reviewUsageUsed', {
                    used: weekly.used,
                    limit: weekly.limit,
                  })}
                </span>
              </div>
              <div className="h-3 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full bg-emerald-500 transition-[width] duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {t('auth.dashboard.reviewUsageRemaining', {
                  remaining: weekly.remaining,
                  date: new Date(weekly.nextResetAt).toLocaleString(),
                })}
              </p>
            </div>

            {sessions.length === 0 ? (
              <div className="rounded-lg border border-dashed py-10 text-center text-sm text-muted-foreground">
                {t('auth.dashboard.reviewUsageEmpty')}
              </div>
            ) : (
              <div className="space-y-3" data-testid="review-session-usage-list">
                {sessions.map(session => (
                  <div
                    key={session.id}
                    className="grid gap-4 rounded-lg border p-4 lg:grid-cols-[minmax(0,1fr)_auto_auto] lg:items-center"
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate font-medium">{session.title}</p>
                        <Badge
                          variant={session.freeMessagesThisWeek > 0 ? 'default' : 'secondary'}
                          className={session.freeMessagesThisWeek > 0
                            ? 'bg-emerald-600 hover:bg-emerald-600'
                            : ''}
                        >
                          {t('auth.dashboard.reviewUsageFreeInSession', {
                            count: session.freeMessagesThisWeek,
                          })}
                        </Badge>
                        {session.paidMessagesThisWeek > 0 && (
                          <Badge variant="outline">
                            {t('auth.dashboard.reviewUsagePaidInSession', {
                              count: session.paidMessagesThisWeek,
                            })}
                          </Badge>
                        )}
                      </div>
                      <p className="mt-1 truncate font-mono text-xs text-muted-foreground">
                        {session.gameId}
                        {' · '}
                        {session.modelName}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                        <span>
                          {t('auth.dashboard.reviewUsageCompleted', {
                            count: session.completedMessages,
                          })}
                        </span>
                        {session.pendingMessages > 0 && (
                          <span>
                            {t('auth.dashboard.reviewUsagePending', {
                              count: session.pendingMessages,
                            })}
                          </span>
                        )}
                        {session.errorMessages > 0 && (
                          <span>
                            {t('auth.dashboard.reviewUsageFailed', {
                              count: session.errorMessages,
                            })}
                          </span>
                        )}
                        <span>
                          {t('auth.dashboard.reviewUsageLastActive', {
                            date: new Date(
                              session.lastMessageAt ?? session.createdAt,
                            ).toLocaleString(),
                          })}
                        </span>
                      </div>
                    </div>

                    <div className="min-w-28 lg:text-right">
                      <p className="text-xs text-muted-foreground">
                        {t('auth.dashboard.reviewUsageConsumption')}
                      </p>
                      <p className="font-mono text-sm tabular-nums">
                        {formatCost(session.consumptionCost)}
                      </p>
                    </div>

                    <div className="min-w-28 lg:text-right">
                      <p className="flex items-center gap-1 text-xs text-muted-foreground lg:justify-end">
                        <ReceiptText className="size-3.5" />
                        {t('auth.dashboard.reviewUsageCharged')}
                      </p>
                      <p className="font-mono text-sm font-semibold tabular-nums">
                        {formatCost(session.chargedCost)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
